import React, { useState } from 'react';
import styled from 'styled-components';
import Header from './Header';
import Footer from './Footer';
import { motion } from 'framer-motion';

const FormModern = ({ embedMode = false }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    email: '',
    aiExperience: '',
    primaryGoal: '',
    aiApplications: [],
    learningPreference: '',
    projectDescription: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleOptionClick = (name, value) => {
    if (name === 'aiApplications') {
      const isSelected = formData[name].includes(value);
      
      if (isSelected) {
        // Remove the value if it's already selected
        setFormData({
          ...formData,
          [name]: formData[name].filter(item => item !== value)
        });
      } else {
        // Add the value if it's not selected and we have less than 3 items
        if (formData[name].length < 3) {
          setFormData({
            ...formData,
            [name]: [...formData[name], value]
          });
        }
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Show loading state
      setIsSubmitting(true);
      
      // Determine if we're in development mode
      const isDev = process.env.NODE_ENV === 'development';
      
      if (isDev) {
        // In development mode, simulate success after a short delay
        console.log('Development mode: Form data would be sent:', formData);
        
        // Simulate network delay
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Simulate success
        setFormSubmitted(true);
      } else {
        // Production mode - send data to our serverless function
        const response = await fetch('/api/send-email', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(formData),
        });
    
        const result = await response.json();
        
        if (response.ok) {
          console.log('Form submitted:', formData);
          setFormSubmitted(true);
        } else {
          console.error('Submission error:', result.error);
          alert('There was an error submitting the form. Please try again.');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      alert('There was an error submitting the form. Please try again.');
    } finally {
      // Reset loading state regardless of success or failure
      setIsSubmitting(false);
    }
  };

  // Render success message if form has been submitted
  if (formSubmitted) {
    return (
      <div>
        {!embedMode && <Header />}
        <FormContainer embedMode={embedMode}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <SuccessMessageContainer>
              <SuccessIcon>✓</SuccessIcon>
              <SuccessTitle>Thank you for submitting your questionnaire!</SuccessTitle>
              <SuccessText>
                We look forward to your coaching session. We'll review your responses to tailor the session to your needs.
              </SuccessText>
            </SuccessMessageContainer>
          </motion.div>
        </FormContainer>
        {!embedMode && <Footer />}
      </div>
    );
  }

  return (
    <div>
      {!embedMode && <Header />}
      <FormContainer embedMode={embedMode}>
        <FormTitle>AI Coaching Session Questionnaire</FormTitle>
        <FormDescription>
          Please complete this form to help us tailor your AI coaching session to your specific needs.
        </FormDescription>
        
        <StyledForm onSubmit={handleSubmit}>
          {/* Section 1: About You */}
          <FormSection>
            <SectionTitle>1. About You</SectionTitle>
            <FormGroup>
              <Label htmlFor="firstName">First Name:</Label>
              <Input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="surname">Surname:</Label>
              <Input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="email">Email:</Label>
              <Input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </FormGroup>
          </FormSection>
          
          {/* Section 2: AI Experience */}
          <FormSection>
            <SectionTitle>2. What is your current level of experience with AI?</SectionTitle>
            <OptionsContainer>
              <OptionButton 
                type="button"
                selected={formData.aiExperience === 'Beginner'}
                onClick={() => handleOptionClick('aiExperience', 'Beginner')}
              >
                <OptionLabel>A</OptionLabel>
                <OptionText>Beginner (no experience or ChatGPT only)</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiExperience === 'Intermediate'}
                onClick={() => handleOptionClick('aiExperience', 'Intermediate')}
              >
                <OptionLabel>B</OptionLabel>
                <OptionText>Intermediate (used multiple models for various use cases)</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiExperience === 'Advanced'}
                onClick={() => handleOptionClick('aiExperience', 'Advanced')}
              >
                <OptionLabel>C</OptionLabel>
                <OptionText>Advanced (worked with AI abilities/systems)</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiExperience === 'Expert'}
                onClick={() => handleOptionClick('aiExperience', 'Expert')}
              >
                <OptionLabel>D</OptionLabel>
                <OptionText>Expert (understand machine learning and neural networks)</OptionText>
              </OptionButton>
            </OptionsContainer>
          </FormSection>
          
          {/* Section 3: Primary Goal */}
          <FormSection>
            <SectionTitle>3. What is your primary goal for this AI coaching session?</SectionTitle>
            <OptionsContainer>
              <OptionButton 
                type="button"
                selected={formData.primaryGoal === 'Understanding AI fundamentals'}
                onClick={() => handleOptionClick('primaryGoal', 'Understanding AI fundamentals')}
              >
                <OptionLabel>A</OptionLabel>
                <OptionText>Understanding AI fundamentals</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.primaryGoal === 'Find an AI solution for a specific problem'}
                onClick={() => handleOptionClick('primaryGoal', 'Find an AI solution for a specific problem')}
              >
                <OptionLabel>B</OptionLabel>
                <OptionText>Find an AI solution for a specific problem</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.primaryGoal === 'Developing an AI strategy for my business'}
                onClick={() => handleOptionClick('primaryGoal', 'Developing an AI strategy for my business')}
              >
                <OptionLabel>C</OptionLabel>
                <OptionText>Developing an AI strategy for my business</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.primaryGoal === 'Evaluating AI abilities to use'}
                onClick={() => handleOptionClick('primaryGoal', 'Evaluating AI abilities to use')}
              >
                <OptionLabel>D</OptionLabel>
                <OptionText>Evaluating AI abilities to use</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.primaryGoal === 'Other'}
                onClick={() => handleOptionClick('primaryGoal', 'Other')}
              >
                <OptionLabel>E</OptionLabel>
                <OptionText>Other</OptionText>
              </OptionButton>
            </OptionsContainer>
          </FormSection>
          
          {/* Section 4: AI Applications */}
          <FormSection>
            <SectionTitle>4. Which of these AI applications would be most valuable in your work or daily life?</SectionTitle>
            <OptionDescription>(select up to 3 options)</OptionDescription>
            <OptionsContainer>
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Writing assistance')}
                onClick={() => handleOptionClick('aiApplications', 'Writing assistance')}
                multi
              >
                <OptionLabel>A</OptionLabel>
                <OptionText>Writing assistance</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Research and Information gathering')}
                onClick={() => handleOptionClick('aiApplications', 'Research and Information gathering')}
                multi
              >
                <OptionLabel>B</OptionLabel>
                <OptionText>Research and Information gathering</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Data analysis and insights')}
                onClick={() => handleOptionClick('aiApplications', 'Data analysis and insights')}
                multi
              >
                <OptionLabel>C</OptionLabel>
                <OptionText>Data analysis and insights</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Image or design creation')}
                onClick={() => handleOptionClick('aiApplications', 'Image or design creation')}
                multi
              >
                <OptionLabel>D</OptionLabel>
                <OptionText>Image or design creation</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Task automation')}
                onClick={() => handleOptionClick('aiApplications', 'Task automation')}
                multi
              >
                <OptionLabel>E</OptionLabel>
                <OptionText>Task automation</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Decision-making support')}
                onClick={() => handleOptionClick('aiApplications', 'Decision-making support')}
                multi
              >
                <OptionLabel>F</OptionLabel>
                <OptionText>Decision-making support</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.aiApplications.includes('Other')}
                onClick={() => handleOptionClick('aiApplications', 'Other')}
                multi
              >
                <OptionLabel>G</OptionLabel>
                <OptionText>Other</OptionText>
              </OptionButton>
            </OptionsContainer>
          </FormSection>
          
          {/* Section 5: Learning Preference */}
          <FormSection>
            <SectionTitle>5. How do you prefer to learn?</SectionTitle>
            <OptionDescription>(optional)</OptionDescription>
            <OptionsContainer>
              <OptionButton 
                type="button"
                selected={formData.learningPreference === 'Hands-on exercises and practical applications'}
                onClick={() => handleOptionClick('learningPreference', 'Hands-on exercises and practical applications')}
              >
                <OptionLabel>A</OptionLabel>
                <OptionText>Hands-on exercises and practical applications</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.learningPreference === 'Theoretical discussions with examples'}
                onClick={() => handleOptionClick('learningPreference', 'Theoretical discussions with examples')}
              >
                <OptionLabel>B</OptionLabel>
                <OptionText>Theoretical discussions with examples</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.learningPreference === 'Case studies and real-world scenarios'}
                onClick={() => handleOptionClick('learningPreference', 'Case studies and real-world scenarios')}
              >
                <OptionLabel>C</OptionLabel>
                <OptionText>Case studies and real-world scenarios</OptionText>
              </OptionButton>
              
              <OptionButton 
                type="button"
                selected={formData.learningPreference === 'Self-paced exploration with some guidance'}
                onClick={() => handleOptionClick('learningPreference', 'Self-paced exploration with some guidance')}
              >
                <OptionLabel>D</OptionLabel>
                <OptionText>Self-paced exploration with some guidance</OptionText>
              </OptionButton>
            </OptionsContainer>
          </FormSection>
          
          {/* Section 6: Project Description */}
          <FormSection>
            <SectionTitle>6. Please briefly describe a specific project or challenge you hope to address with AI</SectionTitle>
            <FormGroup>
              <Textarea
                id="projectDescription"
                name="projectDescription"
                value={formData.projectDescription}
                onChange={handleChange}
                rows="5"
                required
              />
            </FormGroup>
          </FormSection>
          
          <SubmitButton 
            type="submit" 
            disabled={isSubmitting}
            isSubmitting={isSubmitting}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </SubmitButton>
        </StyledForm>
      </FormContainer>
      {!embedMode && <Footer />}
    </div>
  );
};

// Styled Components
const FormContainer = styled.div`
  max-width: 800px;
  margin: ${props => props.embedMode ? '0' : '2rem auto'};
  padding: ${props => props.embedMode ? '0' : '2rem'};
  background-color: ${props => props.embedMode ? 'transparent' : 'var(--white)'};
  border-radius: ${props => props.embedMode ? '0' : '8px'};
  box-shadow: ${props => props.embedMode ? 'none' : 'var(--shadow)'};
`;

const FormTitle = styled.h1`
  color: var(--dark-blue);
  margin-bottom: 1rem;
  text-align: center;
`;

const FormDescription = styled.p`
  text-align: center;
  margin-bottom: 2rem;
  color: var(--text-color);
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const FormSection = styled.div`
  border-bottom: 1px solid #eee;
  padding-bottom: 1.5rem;
  
  &:last-child {
    border-bottom: none;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.2rem;
  color: var(--dark-blue);
  margin-bottom: 0.5rem;
  font-weight: 600;
`;

const OptionDescription = styled.p`
  color: #6b7280;
  font-size: 0.9rem;
  margin-top: 0;
  margin-bottom: 1rem;
  font-style: italic;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 82, 204, 0.2);
  }
`;

const Textarea = styled.textarea`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 1rem;
  resize: vertical;
  
  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 2px rgba(0, 82, 204, 0.2);
  }
`;

const OptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const OptionButton = styled.button`
  display: flex;
  align-items: center;
  padding: 0.75rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: ${props => props.selected ? 'rgba(0, 82, 204, 0.1)' : 'white'};
  border-color: ${props => props.selected ? 'var(--primary-color)' : '#ddd'};
  cursor: pointer;
  transition: all 0.2s;
  text-align: left;
  width: 100%;
  
  &:hover {
    background-color: ${props => props.selected ? 'rgba(0, 82, 204, 0.15)' : '#f9fafb'};
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(0, 82, 204, 0.2);
  }
`;

const OptionLabel = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  background-color: #e5e7eb;
  color: #4b5563;
  font-weight: 600;
  margin-right: 12px;
  flex-shrink: 0;
`;

const OptionText = styled.span`
  flex-grow: 1;
`;

const AddChoiceLink = styled.a`
  color: var(--primary-color);
  text-decoration: none;
  font-weight: 500;
  margin-top: 0.5rem;
  display: inline-block;
  
  &:hover {
    text-decoration: underline;
  }
`;

const SubmitButton = styled.button`
  background-color: ${props => props.isSubmitting ? '#6c8cbf' : 'var(--primary-color)'};
  color: var(--white);
  border: none;
  padding: 1rem;
  border-radius: 4px;
  font-weight: 600;
  font-size: 1rem;
  cursor: ${props => props.isSubmitting ? 'not-allowed' : 'pointer'};
  transition: var(--transition);
  opacity: ${props => props.isSubmitting ? 0.8 : 1};
  
  &:hover {
    background-color: ${props => props.isSubmitting ? '#6c8cbf' : '#003b8a'};
    transform: ${props => props.isSubmitting ? 'none' : 'translateY(-2px)'};
  }
`;

const SuccessMessageContainer = styled.div`
  text-align: center;
  padding: 3rem 2rem;
  max-width: 600px;
  margin: 0 auto;
`;

const SuccessIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background-color: #4CAF50;
  color: white;
  font-size: 3rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 2rem;
`;

const SuccessTitle = styled.h3`
  font-size: 1.8rem;
  color: var(--primary-color);
  margin-bottom: 1rem;
`;

const SuccessText = styled.p`
  font-size: 1.2rem;
  color: var(--text-color);
  line-height: 1.6;
`;

export default FormModern;
